import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { posApi } from '@/services/api';
import {
  createExpenseRouted,
  isLocalFirstMode,
  listLocalCashPockets,
  listLocalBankAccounts,
} from '@/offline';
import { usersApi } from '@/services/api';
import { useSyncStore } from '@/store/syncStore';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';
import { translatePaymentMethod } from '@/utils/translateStatus';

const CATEGORIES = [
  'Tea',
  'Lunch',
  'Water',
  'Cleaning',
  'Stationery',
  'Local Transport',
  'Loading/Unloading',
  'Minor Repairs',
  'Utility Bills',
  'Rent',
  'Salaries',
  'Commission',
  'Fuel',
  'Maintenance',
  'Marketing',
  'Bank Charges',
  'Other',
];

type Props = {
  onClose: () => void;
  onSuccess?: () => void;
};

export default function AddExpenseModal({ onClose, onSuccess }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'Cash' | 'Bank'>('Cash');
  const [postingDate, setPostingDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedPocket, setSelectedPocket] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [remarks, setRemarks] = useState('');
  const [formError, setFormError] = useState('');

  const { data: users } = useQuery({
    queryKey: ['companyUsers'],
    queryFn: async () => {
      const res = await usersApi.list();
      return res.data.message as any[];
    },
    enabled: category === 'Salaries',
  });

  const { data: pockets = [] } = useQuery({
    queryKey: ['cashPockets', useSyncStore((s) => s.posSyncMode)],
    queryFn: async () => {
      if (isLocalFirstMode() || !navigator.onLine || !useSyncStore.getState().isServerReachable) {
        return listLocalCashPockets();
      }
      try {
        const res = await posApi.getCashPockets();
        return res.data.message as any[];
      } catch {
        return listLocalCashPockets();
      }
    },
  });

  const { data: bankAccounts = [] } = useQuery({
    queryKey: ['bankAccounts', useSyncStore((s) => s.posSyncMode)],
    queryFn: async () => {
      if (isLocalFirstMode() || !navigator.onLine || !useSyncStore.getState().isServerReachable) {
        return listLocalBankAccounts();
      }
      try {
        const res = await posApi.getBankAccounts();
        return res.data.message as any[];
      } catch {
        return listLocalBankAccounts();
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => createExpenseRouted(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posExpenses'] });
      qc.invalidateQueries({ queryKey: ['cashPockets'] });
      qc.invalidateQueries({ queryKey: ['bankAccounts'] });
      onSuccess?.();
      onClose();
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || t('expenseModal.failed');
      setFormError(msg);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      setFormError(t('expenseModal.amountRequired'));
      return;
    }
    if (!category) {
      setFormError(t('expenseModal.categoryRequired'));
      return;
    }

    const payload: any = {
      category,
      amount: amt,
      payment_method: paymentMethod,
      posting_date: postingDate,
      remarks,
    };

    if (category === 'Salaries' && selectedEmployee) {
      payload.expense_employee = selectedEmployee;
    }

    if (paymentMethod === 'Cash') {
      if (selectedPocket) {
        payload.cash_pocket = selectedPocket;
      }
    } else {
      if (!selectedBank) {
        setFormError(t('expenseModal.bankRequired'));
        return;
      }
      payload.bank_account = selectedBank;
    }

    createMutation.mutate(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-gray-50 shrink-0">
          <div>
            <h3 className="font-semibold text-gray-950 text-lg">{t('expenseModal.title')}</h3>
            <p className="text-xs text-gray-500">{t('expenseModal.subtitle')}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                className="input-field"
                value={postingDate}
                onChange={(e) => setPostingDate(e.target.value)}
                required
              />
            </div>
            
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('expenseModal.category')}</label>
              <select
                required
                className="input-field"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">{t('expenseModal.selectCategory')}</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{t(`expenseCategories.${cat}`, { defaultValue: cat })}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.amount')} ({getCurrencySymbol()})</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                className="input-field font-mono"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
            {category === 'Salaries' && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <select
                  className="input-field"
                  value={selectedEmployee}
                  onChange={(e) => {
                    setSelectedEmployee(e.target.value);
                    if (e.target.value) {
                      const emp = users?.find((u: any) => u.user === e.target.value);
                      if (emp && emp.salary) {
                        setAmount(emp.salary.toString());
                      }
                    }
                  }}
                >
                  <option value="">Select Employee (Optional)</option>
                  {users?.map((u: any) => (
                    <option key={u.user} value={u.user}>{u.full_name} ({u.role})</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.paymentMethod')}</label>
              <select
                required
                className="input-field"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as 'Cash' | 'Bank')}
              >
                <option value="Cash">{translatePaymentMethod('Cash', t)}</option>
                <option value="Bank">{t('expenseModal.bankAccount')}</option>
              </select>
            </div>

            <div>
              {paymentMethod === 'Cash' ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('expenseModal.payFromDrawer')}</label>
                  <select
                    className="input-field"
                    value={selectedPocket}
                    onChange={(e) => setSelectedPocket(e.target.value)}
                  >
                    <option value="">{t('expenseModal.selectDrawer')}</option>
                    {pockets.map((p) => (
                      <option key={p.name} value={p.name}>
                        {t('expenseModal.drawerBalance', {
                          name: p.pocket_name,
                          balance: formatMoney(p.current_balance),
                        })}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('expenseModal.payFromBank')}</label>
                  <select
                    required
                    className="input-field"
                    value={selectedBank}
                    onChange={(e) => setSelectedBank(e.target.value)}
                  >
                    <option value="">{t('expenseModal.selectBank')}</option>
                    {bankAccounts.map((b) => (
                      <option key={b.name} value={b.name}>
                        {b.bank_name} - {b.account_title}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('expenseModal.remarks')}</label>
            <textarea
              className="input-field min-h-[90px]"
              placeholder={t('expenseModal.remarksPlaceholder')}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          {formError && (
            <div className="bg-red-50 text-red-650 p-3.5 rounded-xl text-sm flex items-start gap-2 border border-red-100">
              <AlertTriangle className="w-5 h-5 shrink-0 text-red-500" />
              <span>{formError}</span>
            </div>
          )}
          </div>

          <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="btn-primary flex-1 py-2.5 flex items-center justify-center gap-1.5"
            >
              {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('expenseModal.submitPayment')}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary py-2.5">
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
